import React, { useEffect, useState, useCallback } from 'react';
import nocApi from '../utils/nocApi';

// ── Theme & styles shared by every NOC screen ─────────────────────────────────
export const t = {
  bg: 'var(--bg)', panel: 'var(--surface)', panelBorder: 'var(--border)', soft: 'var(--surface2, rgba(127,127,127,0.06))',
  cyan: '#2563EB', amber: '#D97706', red: '#DC2626', green: '#16A34A', violet: '#7C3AED', slate: '#64748B',
  textPrimary: 'var(--text)', textMuted: 'var(--muted)', inputBg: 'var(--surface)',
  overlay: 'rgba(15,23,42,0.5)', btnText: '#ffffff',
};
export const panel = { background: t.panel, border: `1px solid ${t.panelBorder}`, borderRadius: 12 };
export const inputCss = { width: '100%', boxSizing: 'border-box', background: t.inputBg, border: `1px solid ${t.panelBorder}`, color: t.textPrimary, borderRadius: 8, padding: '9px 11px', fontSize: 13, fontFamily: 'inherit' };
export const labelCss = { fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 5, display: 'block' };
export const btnPrimary = { background: t.cyan, color: t.btnText, border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' };
export const btnGhost = { background: 'none', color: t.textPrimary, border: `1px solid ${t.panelBorder}`, borderRadius: 8, padding: '8px 13px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' };
export const btnDanger = { ...btnGhost, color: t.red, borderColor: 'rgba(220,38,38,0.35)' };
export const iconBtn = { background: 'none', border: `1px solid ${t.panelBorder}`, borderRadius: 7, padding: '5px 8px', fontSize: 12, cursor: 'pointer', color: t.textPrimary, lineHeight: 1 };
export const th = { padding: '10px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: t.textMuted, textAlign: 'left', whiteSpace: 'nowrap' };
export const td = { padding: '10px 12px', verticalAlign: 'top' };

// ── Status colours ────────────────────────────────────────────────────────────
export function statusColor(s) {
  const v = String(s || '').toLowerCase();
  if (['open', 'down', 'critical', 'cancelled'].includes(v)) return t.red;
  if (['in progress', 'degraded', 'pending', 'on hold'].includes(v)) return t.amber;
  if (['scheduled', 'assigned', 'maintenance'].includes(v)) return t.cyan;
  return t.green;
}
export function StatusPill({ status }) {
  const c = statusColor(status);
  return <span style={{ fontSize: 11, fontWeight: 700, color: c, background: c + '1f', padding: '3px 10px', borderRadius: 99, whiteSpace: 'nowrap' }}>{status || '—'}</span>;
}
export function Badge({ children, color }) {
  const c = color || t.slate;
  return <span style={{ fontSize: 10.5, fontWeight: 700, color: c, background: c + '1a', padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{children}</span>;
}

// ── Formatting ────────────────────────────────────────────────────────────────
export function fmtDT(iso) {
  if (!iso) return '—'; const d = new Date(iso); if (isNaN(d)) return '—';
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
export function fmtD(iso) {
  if (!iso) return '—'; const d = new Date(iso); if (isNaN(d)) return String(iso).slice(0, 10);
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}
export function esc(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

// Permission check: perms is 'all' for admins, else { section: {view,add,edit,delete} }.
export function can(perms, section, action) {
  if (perms === 'all') return true;
  return !!(perms && perms[section] && perms[section][action]);
}

// ── Layout pieces ─────────────────────────────────────────────────────────────
export function Field({ label, children, hint, required }) {
  return (
    <div style={{ marginBottom: 13 }}>
      <label style={labelCss}>{label}{required && <span style={{ color: t.red }}> *</span>}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
export function Row({ children, gap = 12 }) {
  return <div style={{ display: 'flex', gap, flexWrap: 'wrap' }}>{React.Children.map(children, c => c && <div style={{ flex: '1 1 200px', minWidth: 0 }}>{c}</div>)}</div>;
}
export function PageHeader({ title, subtitle, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <h2 style={{ fontSize: 21, fontWeight: 800, margin: 0, color: t.textPrimary }}>{title}</h2>
        {subtitle && <div style={{ fontSize: 12.5, color: t.textMuted, marginTop: 3 }}>{subtitle}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}
export function KpiCard({ label, value, color, sub, onClick }) {
  return (
    <div onClick={onClick} style={{ ...panel, padding: '16px 18px', cursor: onClick ? 'pointer' : 'default', borderTop: `3px solid ${color || t.cyan}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || t.textPrimary, marginTop: 4, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: t.textMuted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}
export function BarList({ items, total, color, empty }) {
  const max = Math.max(1, total || Math.max(...items.map(i => i.count), 1));
  if (!items.length) return <div style={{ fontSize: 12.5, color: t.textMuted }}>{empty || 'No data yet.'}</div>;
  return items.map((it, i) => (
    <div key={i} style={{ marginBottom: 9 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
        <span style={{ color: t.textPrimary }}>{it.label}</span><span style={{ fontWeight: 700 }}>{it.count}</span>
      </div>
      <div style={{ height: 7, background: t.panelBorder, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${Math.round(it.count / max * 100)}%`, height: '100%', background: it.color || color || t.cyan, borderRadius: 4 }} />
      </div>
    </div>
  ));
}
export function Card({ title, right, children, style }) {
  return (
    <div style={{ ...panel, padding: 18, ...style }}>
      {(title || right) && <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, flex: 1 }}>{title}</div>{right}
      </div>}
      {children}
    </div>
  );
}
export function Empty({ children }) { return <div style={{ padding: 30, textAlign: 'center', color: t.textMuted, fontSize: 13 }}>{children}</div>; }

// ── Modal (with width) ───────────────────────────────────────────────────────
export function Modal({ title, subtitle, onClose, children, width = 560, footer }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: t.overlay, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflow: 'auto', padding: '36px 16px' }}>
      <div style={{ ...panel, width, maxWidth: '96vw', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', padding: '18px 22px', borderBottom: `1px solid ${t.panelBorder}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: t.textPrimary }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12.5, color: t.textMuted, marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: t.textMuted, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '18px 22px' }}>{children}</div>
        {footer && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 22px', borderTop: `1px solid ${t.panelBorder}` }}>{footer}</div>}
      </div>
    </div>
  );
}

// ── In-app confirmation (replaces the browser's confirm box) ─────────────────
export function useConfirm() {
  const [state, setState] = useState(null);
  const confirm = useCallback((opts) => new Promise(resolve => setState({ ...opts, resolve })), []);
  const close = (v) => { if (state) state.resolve(v); setState(null); };
  const node = state ? (
    <Modal title={state.title || 'Please confirm'} onClose={() => close(false)} width={440}
      footer={<>
        <button style={btnGhost} onClick={() => close(false)}>Cancel</button>
        <button style={state.danger ? { ...btnPrimary, background: t.red } : btnPrimary} onClick={() => close(true)}>{state.confirmLabel || 'Confirm'}</button>
      </>}>
      <div style={{ fontSize: 13.5, color: t.textPrimary, lineHeight: 1.55 }}>{state.message}</div>
    </Modal>
  ) : null;
  return [confirm, node];
}

// ── Toast ─────────────────────────────────────────────────────────────────────
export function useToast() {
  const [msg, setMsg] = useState(null);
  const show = useCallback((text, kind = 'ok') => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 3500); }, []);
  const node = msg ? (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, background: msg.kind === 'err' ? t.red : '#0f172a', color: '#fff',
      padding: '12px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: '0 10px 30px rgba(0,0,0,0.25)', maxWidth: 420 }}>{msg.text}</div>
  ) : null;
  return [show, node];
}

// ── Admin-defined fields ─────────────────────────────────────────────────────
export function useFields(section) {
  const [fields, setFields] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    nocApi.get(`/fields/${section}`).then(r => setFields(Array.isArray(r) ? r : [])).catch(() => setFields([])).finally(() => setLoaded(true));
  }, [section]);
  useEffect(() => { load(); }, [load]);
  return [fields, load, loaded];
}

// Threshold check used for live warnings. Returns a message or null.
export function checkThreshold(th, raw) {
  if (!th || raw === '' || raw === null || raw === undefined) return null;
  const num = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
  if (isNaN(num)) return null;
  if (th.mode === 'range') {
    if (th.min !== '' && th.min != null && num < Number(th.min)) return `Below optimal minimum (${th.min})`;
    if (th.max !== '' && th.max != null && num > Number(th.max)) return `Above optimal maximum (${th.max})`;
    return null;
  }
  if (th.value === '' || th.value == null) return null;
  const v = Number(th.value), op = th.op || '>';
  const bad = (op === '>' && num > v) || (op === '>=' && num >= v) || (op === '<' && num < v) || (op === '<=' && num <= v) || (op === '=' && num === v) || (op === '!=' && num !== v);
  return bad ? `Alarm condition (${op} ${v})` : null;
}
export function thresholdText(th) {
  if (!th) return '';
  if (th.mode === 'range') {
    if (th.min != null && th.max != null) return `Optimal ${th.min} – ${th.max}`;
    if (th.min != null) return `Optimal ≥ ${th.min}`;
    if (th.max != null) return `Optimal ≤ ${th.max}`;
    return '';
  }
  return `Alarm if ${th.op} ${th.value}`;
}

export function FieldInput({ f, value, onChange }) {
  const warn = checkThreshold(f.threshold, value);
  const style = { ...inputCss, ...(warn ? { borderColor: t.red, background: 'rgba(220,38,38,0.06)' } : {}) };
  let input;
  if (f.field_type === 'textarea') input = <textarea rows={3} style={{ ...style, resize: 'vertical' }} value={value || ''} onChange={e => onChange(e.target.value)} />;
  else if (f.field_type === 'select') input = (
    <select style={style} value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">Select…</option>{(f.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
    </select>);
  else if (f.field_type === 'checkbox') input = (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '8px 0' }}>
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} /> Yes
    </label>);
  else input = <input type={f.field_type === 'number' ? 'number' : f.field_type === 'date' ? 'date' : 'text'} step="any" style={style} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
  return (
    <Field label={f.label} required={!!f.required} hint={!warn ? (f.help || thresholdText(f.threshold) || null) : null}>
      {input}
      {warn && <div style={{ fontSize: 11.5, color: t.red, marginTop: 4, fontWeight: 700 }}>⚠ {warn}. Please double-check this parameter.</div>}
    </Field>
  );
}

// Renders admin-defined fields; grouped by group_name when present.
export function CustomFields({ fields, values, onChange, grouped, columns = 2 }) {
  if (!fields || !fields.length) return null;
  const grid = { display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, columnGap: 14 };
  if (!grouped) {
    return <div style={grid}>{fields.map(f => <div key={f.id} style={f.field_type === 'textarea' ? { gridColumn: '1 / -1' } : null}><FieldInput f={f} value={values[f.field_key]} onChange={v => onChange(f.field_key, v)} /></div>)}</div>;
  }
  const groups = [];
  fields.forEach(f => { const g = f.group_name || 'Additional'; let e = groups.find(x => x.name === g); if (!e) { e = { name: g, fields: [] }; groups.push(e); } e.fields.push(f); });
  return groups.map(g => (
    <div key={g.name} style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.5px', color: t.cyan, margin: '6px 0 10px', paddingBottom: 6, borderBottom: `1px solid ${t.panelBorder}` }}>{g.name}</div>
      <div style={grid}>{g.fields.map(f => <div key={f.id} style={f.field_type === 'textarea' ? { gridColumn: '1 / -1' } : null}><FieldInput f={f} value={values[f.field_key]} onChange={v => onChange(f.field_key, v)} /></div>)}</div>
    </div>
  ));
}

// Check required admin fields before saving; returns missing labels.
export function missingRequired(fields, values) {
  return (fields || []).filter(f => f.required && (values[f.field_key] === undefined || values[f.field_key] === '' || values[f.field_key] === null)).map(f => f.label);
}

// Show saved custom values read-only (detail views).
export function CustomValues({ fields, values }) {
  const list = (fields || []).filter(f => values && values[f.field_key] !== undefined && values[f.field_key] !== '');
  if (!list.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '8px 16px', marginTop: 8 }}>
      {list.map(f => <KV key={f.id} k={f.label} v={typeof values[f.field_key] === 'boolean' ? (values[f.field_key] ? 'Yes' : 'No') : String(values[f.field_key])} />)}
    </div>
  );
}
export function KV({ k, v, wide }) {
  return (
    <div style={wide ? { gridColumn: '1 / -1' } : null}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '.4px' }}>{k}</div>
      <div style={{ fontSize: 13, color: t.textPrimary, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v || '—'}</div>
    </div>
  );
}

// Print-friendly window (save as PDF).
export function openPrintWindow(title, bodyHtml) {
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${esc(title)}</title>
<style>@page{margin:14mm 12mm;}body{font-family:-apple-system,Segoe UI,Arial,sans-serif;color:#111;margin:0;padding:20px;}
h1{font-size:16px;margin:0 0 4px;}h2{font-size:13px;margin:22px 0 8px;padding-top:14px;border-top:2px solid #222;}
.meta{color:#666;font-size:11px;margin-bottom:16px;}table{width:100%;border-collapse:collapse;font-size:10.5px;}
th,td{text-align:left;padding:5px 6px;border-bottom:1px solid #ddd;}th{background:#f2f2f2;font-size:9px;text-transform:uppercase;letter-spacing:.4px;color:#444;}
.kv{font-size:12px;margin:4px 0;}.kv b{display:inline-block;width:170px;color:#333;}.flag{color:#b91c1c;font-weight:700;}
.print-hint{background:#eef6fb;border:1px solid #bfe0ee;border-radius:6px;padding:10px 14px;font-size:12px;margin-bottom:16px;}
@media print{.print-hint{display:none;}}</style></head>
<body><div class="print-hint">Press Ctrl/Cmd+P and choose <b>"Save as PDF"</b>.</div>${bodyHtml}</body></html>`;
  const win = window.open('', '_blank');
  if (!win) { alert('Please allow pop-ups to export as PDF.'); return; }
  win.document.open(); win.document.write(html); win.document.close();
}
